import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppearance, useUpdateAppearanceSettings } from '~/data/appearance';
import { useLanguages } from '~/data/languages';
import { fetchFromOldApi } from '~/services/Http';

import { deleteHomePageBG, postHomePageSettings } from './data';

type GreetingText = {
  [key: string]: string;
};

export const HomePageSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { languages, isLoading: languagesLoading } = useLanguages();
  const { customAppearance, isLoading: appearanceLoading } = useAppearance();
  const updateAppearanceSettings = useUpdateAppearanceSettings();

  const [activeGreetingTextLanguage, setActiveGreetingTextLanguage] =
    React.useState<string>('en');
  const [greetingText, setGreetingText] = React.useState<GreetingText>({});
  const [homePageImage, setHomePageImage] = React.useState<File>();
  const [showAnnouncement, setShowAnnouncement] =
    React.useState<boolean>(false);

  const loading = appearanceLoading || languagesLoading;

  const urlToImage = async (pictureId: string) => {
    const response = await fetchFromOldApi(
      `/json/custom/appearance/picture/${pictureId}`,
    );
    const blob = await response.blob();
    return new File([blob], `${pictureId}.jpg`, { type: blob.type });
  };

  useEffect(() => {
    if (!loading) {
      const defaultGreetinText = languages.reduce(
        (obj, item) =>
          Object.assign(obj, {
            [item.fComLanguageCode]: customAppearance.fGreetingTranslations
              ? customAppearance.fGreetingTranslations.find(
                  (trans) => trans.fLang === item.fComLanguageCode,
                )?.fGreetingText || ''
              : '',
          }),
        {},
      );
      (async () => {
        if (customAppearance.fAppearanceBackground) {
          setHomePageImage(
            await urlToImage(customAppearance.fAppearanceBackground),
          );
        }
      })();
      setGreetingText(defaultGreetinText);
      setShowAnnouncement(customAppearance.fShowAnnouncement || false);
    }
  }, [customAppearance, languages, loading]);

  const onSave = async () => {
    try {
      const greetingTextData = Object.keys(greetingText).map((key) => ({
        fLang: key,
        fGreetingText: greetingText[key],
      }));
      const formData = new FormData();
      formData.append('file', homePageImage || '');
      formData.append('fBackground', homePageImage ? homePageImage.name : '');
      formData.append(
        'fShowAnnouncement',
        showAnnouncement !== undefined ? showAnnouncement.toString() : 'false',
      );
      formData.append(
        'fGreetingTranslations',
        JSON.stringify(greetingTextData),
      );
      formData.append(
        'fTopBarColors.fBackgroundColor',
        customAppearance.fTopBarColors
          ? customAppearance.fTopBarColors.fBackgroundColor
          : '',
      );
      formData.append(
        'fTopBarColors.fIconsColor',
        customAppearance.fTopBarColors
          ? customAppearance.fTopBarColors.fIconsColor
          : '',
      );
      formData.append(
        'fLeftSideMenuColors.fBackgroundColor',
        customAppearance.fLeftSideMenuColors
          ? customAppearance.fLeftSideMenuColors.fBackgroundColor
          : '',
      );
      formData.append(
        'fLeftSideMenuColors.fIconsColor',
        customAppearance.fLeftSideMenuColors
          ? customAppearance.fLeftSideMenuColors.fIconsColor
          : '',
      );
      await postHomePageSettings(formData).then((data) => {
        updateAppearanceSettings(data);
      });
      if (!homePageImage && customAppearance.fAppearanceBackground) {
        await deleteHomePageBG(customAppearance.fAppearanceBackground);
      }
      enqueueSnackbar(t('message_success_approval'), {
        variant: 'success',
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('laGeneralError'), {
        variant: 'error',
      });
    }
  };

  if (appearanceLoading || languagesLoading || languages.length === 0) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ width: '800px' }}>
      <Stack spacing={4}>
        <Box>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('greetingText')}:
          </Typography>
          <Tabs
            value={activeGreetingTextLanguage}
            onChange={(_: unknown, lng: string) => {
              setActiveGreetingTextLanguage(lng);
            }}
          >
            {languages.map((language) => {
              return (
                <Tab
                  key={language.fComLanguageCode}
                  label={language.fComLanguageName}
                  value={language.fComLanguageCode}
                />
              );
            })}
          </Tabs>
          {languages.map((language) => (
            <Box
              key={language.fComLanguageCode}
              sx={{
                display:
                  language.fComLanguageCode === activeGreetingTextLanguage
                    ? 'block'
                    : 'none',
                maxWidth: '750px',
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={
                  greetingText[
                    language.fComLanguageCode as keyof GreetingText
                  ] || ''
                }
                onChange={(e) =>
                  setGreetingText({
                    ...greetingText,
                    [language.fComLanguageCode]: e.target.value,
                  })
                }
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('homePageBGImage')}
          </Typography>
          <Box>
            <label htmlFor={`upload-homepage-image`}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`upload-homepage-image`}
                type="file"
                onChange={(e) => {
                  setHomePageImage(
                    e.target.files ? e.target.files[0] : undefined,
                  );
                  e.currentTarget.value = '';
                }}
              />
              <Button
                component="span"
                sx={{ maxWidth: '130px', textTransform: 'none' }}
                startIcon={<AddIcon />}
              >
                {t('laAddImage')}
              </Button>
            </label>
            <Button
              onClick={() => {
                setHomePageImage(undefined);
              }}
              component="span"
              sx={{ width: '140px', textTransform: 'none' }}
              startIcon={<DeleteIcon />}
            >
              {t('laRemoveImage')}
            </Button>
          </Box>
          {homePageImage && (
            <img
              src={URL.createObjectURL(homePageImage)}
              alt="homepage"
              width="256"
              height="192"
            />
          )}
        </Box>
        <Box
          sx={{ display: 'flex', direction: 'column', alignItems: 'center' }}
        >
          <Checkbox
            checked={showAnnouncement}
            onChange={() => setShowAnnouncement(!showAnnouncement)}
          />
          <Typography>{t('showAnnouncementHomepage')}</Typography>
        </Box>
      </Stack>
      <Button sx={{ float: 'right' }} onClick={onSave} variant="contained">
        {t('laSave')}
      </Button>
    </Box>
  );
};
